/**
 * Liked Watchlists Cache Utility
 *
 * This module provides a simple in-memory cache for liked watchlists to prevent
 * redundant Firebase calls. The cache is especially useful because:
 * - Multiple components need to know which watchlists the user has liked
 * - The same getLikedWatchlists() call was being made 10+ times per page load
 * - The data rarely changes during a single session
 *
 * The cache has a 2-minute TTL (shorter than profile cache since likes change more often).
 */

import { socialService } from '@watchlist/shared';

// Type matching what socialService.getLikedWatchlists returns
interface LikedWatchlist {
  id: string;
  name: string;
  description?: string;
  movieDetails: any[];
  isPrivate: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  collaborators?: string[];
  followerCount?: number;
}

interface CachedLikedWatchlistsEntry {
  watchlists: LikedWatchlist[];
  fetchedAt: number;
}

// In-memory cache with 2-minute TTL
const CACHE_TTL_MS = 2 * 60 * 1000;
const likedWatchlistsCache = new Map<string, CachedLikedWatchlistsEntry>();
const pendingFetches = new Map<string, Promise<LikedWatchlist[]>>();

/**
 * Get cached liked watchlists for a user, or fetch if not cached/expired
 */
export async function getCachedLikedWatchlists(userId: string): Promise<LikedWatchlist[]> {
  if (!userId || typeof userId !== 'string' || userId.trim() === '') {
    return [];
  }

  const now = Date.now();
  const cached = likedWatchlistsCache.get(userId);

  // Return cached if valid and not expired (silent - no log for cache hits)
  if (cached && (now - cached.fetchedAt) < CACHE_TTL_MS) {
    return cached.watchlists;
  }

  // Check if there's already a pending fetch for this user (prevent duplicate requests)
  const pendingFetch = pendingFetches.get(userId);
  if (pendingFetch) {
    console.log(`⏳ LikedWatchlistsCache: Reusing pending fetch for ${userId}`);
    return pendingFetch;
  }

  // Fetch from Firebase
  console.log(`🔄 LikedWatchlistsCache: Fetching liked watchlists for ${userId}`);
  const fetchPromise = (async (): Promise<LikedWatchlist[]> => {
    try {
      const watchlists = await socialService.getLikedWatchlists(userId);
      likedWatchlistsCache.set(userId, {
        watchlists,
        fetchedAt: Date.now()
      });
      console.log(`✅ LikedWatchlistsCache: Cached ${watchlists.length} liked watchlists for ${userId}`);
      return watchlists;
    } catch (error) {
      console.warn(`LikedWatchlistsCache: Failed to fetch liked watchlists for ${userId}:`, error);
      return [];
    } finally {
      pendingFetches.delete(userId);
    }
  })();

  pendingFetches.set(userId, fetchPromise);
  return fetchPromise;
}

/**
 * Get liked watchlists from cache synchronously (returns null if not cached)
 * Useful for quick checks without triggering a fetch
 */
export function getLikedWatchlistsFromCache(userId: string): LikedWatchlist[] | null {
  if (!userId) return null;

  const now = Date.now();
  const cached = likedWatchlistsCache.get(userId);

  if (cached && (now - cached.fetchedAt) < CACHE_TTL_MS) {
    return cached.watchlists;
  }

  return null;
}

/**
 * Check if a specific watchlist is liked (from cache only)
 * Returns undefined if cache miss (caller should fetch)
 */
export function isWatchlistLikedFromCache(userId: string, watchlistId: string): boolean | undefined {
  const cached = getLikedWatchlistsFromCache(userId);
  if (cached === null) return undefined;
  return cached.some(w => w.id === watchlistId);
}

/**
 * Invalidate the cache for a user (call after liking/unliking)
 */
export function invalidateLikedWatchlistsCache(userId: string): void {
  likedWatchlistsCache.delete(userId);
  console.log(`🧹 LikedWatchlistsCache: Invalidated cache for ${userId}`);
}

/**
 * Add a watchlist to the cache optimistically (after liking)
 * This avoids refetching the entire list
 */
export function addToLikedWatchlistsCache(userId: string, watchlist: LikedWatchlist): void {
  const cached = likedWatchlistsCache.get(userId);
  if (cached) {
    // Check if not already in cache
    if (!cached.watchlists.some(w => w.id === watchlist.id)) {
      cached.watchlists.push(watchlist);
      console.log(`➕ LikedWatchlistsCache: Added ${watchlist.id} to cache for ${userId}`);
    }
  }
}

/**
 * Remove a watchlist from the cache optimistically (after unliking)
 * This avoids refetching the entire list
 */
export function removeFromLikedWatchlistsCache(userId: string, watchlistId: string): void {
  const cached = likedWatchlistsCache.get(userId);
  if (cached) {
    cached.watchlists = cached.watchlists.filter(w => w.id !== watchlistId);
    console.log(`➖ LikedWatchlistsCache: Removed ${watchlistId} from cache for ${userId}`);
  }
}

/**
 * Clear the entire cache (useful on logout)
 */
export function clearLikedWatchlistsCache(): void {
  likedWatchlistsCache.clear();
  pendingFetches.clear();
  console.log('🧹 LikedWatchlistsCache: Cache cleared');
}

/**
 * Get cache statistics for debugging
 */
export function getLikedWatchlistsCacheStats(): { usersCached: number; pendingFetches: number } {
  return {
    usersCached: likedWatchlistsCache.size,
    pendingFetches: pendingFetches.size
  };
}
